import React, { useContext, useEffect, useRef } from 'react';
import { UserDataContext } from 'lane-shared/contexts';
import { useAuth0 } from '@auth0/auth0-react';
import LoadingStates from './components/general/LoadingStates';
import { Redirect, useLocation } from 'react-router-dom';
import { routes } from 'lane-shared/config';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import { useAuthClearSession } from './components/onboarding/Onboarding/useAuthClearSession';

/**
 * When FF on: only clear session (Auth0 logout → identifier) when sessionInvalidatedByMeError
 * (me query failed with auth error, e.g. expired/blocked). Otherwise redirect to signup (new SSO).
 * When FF off (legacy): redirect to sign-up when needsToSignup.
 */
function useClearSessionAndRedirectToIdentifier(
  needsToSignup: boolean,
  hasAttemptedLogin: boolean,
  sessionInvalidatedByMeErrorWhenFFOn: boolean,
  location: ReturnType<typeof useLocation>
) {
  const identifierUrl =
    window.location.origin +
    routes.identifier +
    (location.search || '');
  const clearSession = useAuthClearSession({
    returnTo: identifierUrl,
    emitLogout: false,
  });
  const hasStartedClear = useRef(false);

  useEffect(() => {
    if (
      !needsToSignup ||
      !hasAttemptedLogin ||
      !sessionInvalidatedByMeErrorWhenFFOn ||
      hasStartedClear.current
    ) {
      return;
    }

    hasStartedClear.current = true;

    clearSession().catch(() => {
      // Redirect still happens via auth0Logout; ignore cleanup errors
    });
  }, [
    needsToSignup,
    hasAttemptedLogin,
    sessionInvalidatedByMeErrorWhenFFOn,
    clearSession,
  ]);
}

export const RequireUserOnboarded = ({
  children,
}: {
  children: React.ReactElement;
}) => {
  const {
    user,
    isLoggingIn: isLoggingInUser,
    hasAttemptedLogin,
    sessionInvalidatedByMeError,
  } = useContext(UserDataContext);

  const {
    isAuthenticated: isAuthenticatedViaAuth0,
    isLoading: isLoadingAuth0Authentication,
  } = useAuth0();

  const location = useLocation();

  const redirectExpiredSessionToSignIn = Boolean(
    useFlag(FeatureFlag.EnableOAuthAuth0UserDetection, false)
  );

  const needsToSignup = isAuthenticatedViaAuth0 && !user;
  const needsToOnboard = isAuthenticatedViaAuth0 && !user?.roles?.length;

  // Only use sessionInvalidatedByMeError when FF is on; when FF off we never clear (legacy).
  const sessionInvalidatedByMeErrorWhenFFOn = redirectExpiredSessionToSignIn
    ? sessionInvalidatedByMeError
    : false;

  useClearSessionAndRedirectToIdentifier(
    needsToSignup,
    hasAttemptedLogin,
    sessionInvalidatedByMeErrorWhenFFOn,
    location
  );

  if (isLoadingAuth0Authentication || isLoggingInUser) {
    return <LoadingStates />;
  }

  if (!hasAttemptedLogin) {
    return <LoadingStates />;
  }

  const queryParams = location.search.substring(1);

  if (needsToSignup) {
    // When FF on and session was invalidated by me error (e.g. expired/blocked),
    // show loading so effect runs clearSession() → identifier. Otherwise redirect to signup (new SSO).
    if (
      redirectExpiredSessionToSignIn &&
      sessionInvalidatedByMeErrorWhenFFOn
    ) {
      return <LoadingStates />;
    }

    return (
      <Redirect
        to={{
          pathname: `${routes.signup}/profile`,
          search: queryParams,
        }}
      />
    );
  }

  if (needsToOnboard) {
    return (
      <Redirect
        to={{
          pathname: `${routes.onboarding}/profile`,
          search: queryParams,
        }}
      />
    );
  }

  return children;
};
